import { forwardRef, useImperativeHandle, useState, useCallback, DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudUpload, faFile, faXmark } from '@fortawesome/pro-light-svg-icons';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@shared/components/ui/shadcn/dialog';
import { Button } from '@shared/components/ui/button';
import { useUploadSignedPdf } from '../../hooks/useUploadSignedPdf';
import { useCompleteManifest } from '../../hooks/useCompleteManifest';
import { Manifest } from '../../models';
import { useToast } from '@shared/hooks/use-toast';

export interface UploadSignedPdfModalRef {
  open: (manifest: Manifest) => void;
  close: () => void;
}

interface UploadSignedPdfModalProps {
  onSuccess?: () => void;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024;

export const UploadSignedPdfModal = forwardRef<UploadSignedPdfModalRef, UploadSignedPdfModalProps>(
  ({ onSuccess }, ref) => {
    const { t } = useTranslation('manifestsModule');
    const { toast } = useToast();
    const [isOpen, setIsOpen] = useState(false);
    const [manifest, setManifest] = useState<Manifest | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isDragOver, setIsDragOver] = useState(false);

    const uploadMutation = useUploadSignedPdf();
    const completeMutation = useCompleteManifest();

    const resetState = useCallback(() => {
      setSelectedFile(null);
      setIsDragOver(false);
    }, []);

    useImperativeHandle(ref, () => ({
      open: (manifestData: Manifest) => {
        setManifest(manifestData);
        setIsOpen(true);
        resetState();
      },
      close: () => {
        setIsOpen(false);
        setManifest(null);
        resetState();
      },
    }));

    const validateFile = (file: File): boolean => {
      if (file.type !== 'application/pdf') {
        toast({
          title: t('modal.upload.validationError.title', 'Error de validacion'),
          description: t('modal.upload.validationError.invalidType', 'Solo se permiten archivos PDF'),
          variant: 'destructive',
        });
        return false;
      }

      if (file.size > MAX_FILE_SIZE) {
        toast({
          title: t('modal.upload.validationError.title', 'Error de validacion'),
          description: t('modal.upload.validationError.tooLarge', 'El archivo no debe superar 5MB'),
          variant: 'destructive',
        });
        return false;
      }

      return true;
    };

    const handleFileSelect = (file: File) => {
      if (validateFile(file)) {
        setSelectedFile(file);
      }
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    };

    const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDragOver(true);
    };

    const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDragOver(false);
    };

    const handleDrop = (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDragOver(false);
      const file = event.dataTransfer.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    };

    const handleRemoveFile = () => {
      setSelectedFile(null);
    };

    const handleSubmit = async () => {
      if (!manifest || !selectedFile) return;

      try {
        await uploadMutation.mutateAsync({
          manifestId: manifest.id,
          file: selectedFile,
        });
        await completeMutation.mutateAsync(manifest.id);
        setIsOpen(false);
        setManifest(null);
        resetState();
        onSuccess?.();
      } catch (error) {
        // Error handling is done in the mutation hooks
      }
    };

    const isLoading = uploadMutation.isPending || completeMutation.isPending;

    const formatFileSize = (bytes: number): string => {
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    return (
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('modal.upload.title', 'Subir PDF Firmado')}</DialogTitle>
          </DialogHeader>

          <div className="py-4">
            {manifest && (
              <p className="text-sm text-muted-foreground mb-4">
                {t('modal.upload.manifest', 'Manifiesto')}: {manifest.manifest_number}
              </p>
            )}

            {!selectedFile ? (
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`
                border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
                transition-colors duration-200
                ${isDragOver ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}
              `}
                onClick={() => document.getElementById('pdf-upload')?.click()}
              >
                <FontAwesomeIcon icon={faCloudUpload} className="w-12 h-12 text-muted-foreground mb-4" />
                <p className="text-sm text-muted-foreground">
                  {t('modal.upload.uploadArea', 'Arrastra el PDF aqui o haz clic para seleccionar')}
                </p>
                <p className="text-xs text-muted-foreground mt-2">{t('modal.upload.maxSize', 'Maximo 5MB')}</p>
                <input
                  id="pdf-upload"
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </div>
            ) : (
              <div className="border rounded-lg p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faFile} className="w-8 h-8 text-red-500" />
                  <div>
                    <p className="text-sm font-medium text-text truncate max-w-[200px]">{selectedFile.name}</p>
                    <p className="text-xs text-muted-foreground">{formatFileSize(selectedFile.size)}</p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={handleRemoveFile} className="h-8 w-8 p-0">
                  <FontAwesomeIcon icon={faXmark} className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsOpen(false)} disabled={isLoading}>
              {t('modal.upload.cancel', 'Cancelar')}
            </Button>
            <Button onClick={handleSubmit} disabled={!selectedFile || isLoading}>
              {isLoading ? t('modal.upload.uploading', 'Subiendo...') : t('modal.upload.complete', 'Subir y Completar')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  },
);

UploadSignedPdfModal.displayName = 'UploadSignedPdfModal';
